import { useEffect, useRef, useState } from 'react';

import { LOADING_TIMING } from '../config/loadingTiming';

/**
 * The timing rules of docs/platform-standards.md §14, as one hook.
 *
 *   const { visible, hint, timedOut } = useDelayedBusy(isLoading);
 *
 * `busy` is whether the surface is waiting on the server. The hook turns that
 * into what the person should SEE:
 *
 *   visible   the placeholder (skeleton, spinner, refresh cue) may show. It is
 *             false for the first LOADING_DELAY_MS of a wait, so an answer
 *             that is quick never flashes one; and once it is true it stays
 *             true for at least LOADING_MIN_VISIBLE_MS, so it never blinks out.
 *   hint      the wait has run LOADING_HINT_MS: add "Still loading".
 *   timedOut  the wait has run LOADING_TIMEOUT_MS: stop waiting, show the
 *             error with Retry.
 *
 * `hint` and `timedOut` count from the moment `busy` last turned true and
 * clear the moment it turns false. The numbers come from
 * `src/config/loadingTiming.js` and nowhere else; `timing` exists so the
 * gallery and tests can state a different wait, never so a surface can.
 *
 * Only timers are used (no clock reads): a fake-timer test advances every
 * rule, and nothing here depends on the wall clock.
 */
export default function useDelayedBusy(busy, timing) {
	const { delay, minVisible, hint, timeout } = { ...LOADING_TIMING, ...timing };

	const [shown, setShown] = useState(false);
	const [hintDue, setHintDue] = useState(false);
	const [timedOut, setTimedOut] = useState(false);

	// The visibility rules read the latest `busy` from a timer, so it lives in
	// a ref. `held` is true from the moment the placeholder shows until its
	// minimum time is up.
	// It is written during render, not in an effect: a render that straddles
	// the delay commits the answer, and the due reveal timer fires before that
	// commit's effects run. Read from the effect, `busy` would still be true.
	const busyRef = useRef(busy);
	busyRef.current = busy;
	const shownRef = useRef(false);
	const heldRef = useRef(false);
	const holdTimer = useRef(null);

	useEffect(() => {
		const reveal = () => {
			// The wait ended while this timer was already due: nothing to show.
			if (!busyRef.current) return;
			shownRef.current = true;
			heldRef.current = true;
			setShown(true);
			clearTimeout(holdTimer.current);
			holdTimer.current = setTimeout(() => {
				holdTimer.current = null;
				heldRef.current = false;
				// Still waiting: stay. The wait ending is what hides it.
				if (busyRef.current) return;
				shownRef.current = false;
				setShown(false);
			}, minVisible);
		};

		if (busy) {
			// Already showing (a wait that began inside the hold of the last
			// one): keep the one placeholder, start nothing new.
			if (shownRef.current) return undefined;
			if (delay <= 0) {
				reveal();
				return undefined;
			}
			const timer = setTimeout(reveal, delay);
			return () => clearTimeout(timer);
		}

		// The wait is over. Inside the hold the placeholder stays until the hold
		// timer takes it down; after it, it goes now.
		if (shownRef.current && !heldRef.current) {
			shownRef.current = false;
			setShown(false);
		}
		return undefined;
	}, [busy, delay, minVisible]);

	useEffect(() => {
		if (!busy) {
			setHintDue(false);
			setTimedOut(false);
			return undefined;
		}
		const hintTimer = setTimeout(() => setHintDue(true), hint);
		const timeoutTimer = setTimeout(() => setTimedOut(true), timeout);
		return () => {
			clearTimeout(hintTimer);
			clearTimeout(timeoutTimer);
		};
	}, [busy, hint, timeout]);

	// Nothing may fire after the surface is gone.
	useEffect(() => () => clearTimeout(holdTimer.current), []);

	return {
		// With no delay the first render already shows it: no frame of nothing.
		visible: shown || (busy && delay <= 0),
		hint: busy && hintDue,
		timedOut: busy && timedOut,
	};
}
